import {UserAddOutlined} from '@ant-design/icons';
import {Alert, Button, Card, ConfigProvider, Dropdown, Menu, Skeleton, Spin, Tag} from 'antd';
import {format} from 'date-fns';
import {useState} from 'react';
import toast from 'react-hot-toast';
import {AiFillProduct} from 'react-icons/ai';
import {BsFillBoxSeamFill} from 'react-icons/bs';
import {FaUserPlus} from 'react-icons/fa';
import {IoIosWarning} from 'react-icons/io';
import {useSelector} from 'react-redux';
import {Link, useNavigate} from 'react-router-dom';

import {RQGetOrderAPI, RQGetUserAPI, RqGetAllProducts, RqUpdateOrder} from '../../../reactQuery/apis';
import {selectAuth} from '../../../redux/selectors';

import {LowStockModal} from './LowStockModal';

const UserCountScreen = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const auth = useSelector(selectAuth);

  const token = auth?.tokenData;

  const id = token ? JSON.parse(atob(token.split('.')[1]))?.userId : null;

  const {data: userData, isLoading} = RQGetUserAPI(id, token);

  const userCount = Array.isArray(userData) ? userData.length : 0;

  const {
    data: allProducts = [],
    isLoading: isProductsLoading,
    isError: isProductError,
    error: productError,
  } = RqGetAllProducts(token);

  const {
    data: orderData,
    isLoading: isOrdersLoading,
  } = RQGetOrderAPI(id, token);

  const LOW_STOCK_LIMIT = 5;

  const lowStockProducts = allProducts.filter(product =>
    Array.isArray(product?.variants) &&
  product.variants.some(variant =>
    Array.isArray(variant?.sizes) &&
    variant.sizes.some(size =>
      Number(size?.quantity ?? 0) <= LOW_STOCK_LIMIT,
    ),
  ),
  );

  // Status Created 
  const updateOrderMutation = RqUpdateOrder();

  const handleStatusChange = async (id, currentStatus, newStatus) => {
    if (newStatus === currentStatus) {return;}

    try {
      const record = orders.find(o => o.id === id);

      await updateOrderMutation.mutateAsync({
        token,
        id,
        formData: {...record, status: newStatus},
      });

      toast.success(`Order updated to ${newStatus}`);
    } catch (err) {
      toast.error('Failed to update order');
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case 'CONFIRMED':
        return 'green';
      case 'PENDING':
        return 'orange';
      case 'CANCELLED':
        return 'red';
      case 'SHIPPED':
        return 'blue';
      case 'DELIVERED':
        return 'purple';
      case 'CREATED':
        return 'gold'; 
      default:
        return 'default';
    }
  };

  const lowStockCount = lowStockProducts.length;

  if (isLoading || isProductsLoading) {
    return (
      <div className={'flex justify-center items-center h-screen bg-primary-100'}>
        <Spin size={'large'} />
      </div>
    );
  }

  const orders = Array.isArray(orderData) ? orderData : [];

  const todayDate = format(new Date(), 'yyyy-MM-dd');

  const todaysOrders = orders.filter(order =>
    order.createdOn?.startsWith(todayDate),
  );

  const orderCount = Array.isArray(orderData) ? orderData.length : 0;
  const productCount = Array.isArray(allProducts) ? allProducts.length : 0;

  const createdOrders = orders
    .filter(order => order.status === 'CREATED')
    .sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn));

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#ffffff',
        },
      }}
    >
      <p className={'text-lg font-bold mb-4'}> Dashboard</p>
      <div className={' p-0 md:p-8 text-black min-h-[80vh]'}>
        
        <div className={'flex w-full flex-wrap gap-8 justify-center'}>

          {/* User Card */}
          <div className={'flex'}>
            <Link to={'/admin/user'} className={'w-full sm:w-[280px] lg:w-[300px]'}>
              <Card
                variant={'borderless'}
                className={'transition-all transform-gpu hover:scale-[1.04] bg-gradient-to-tr from-primary-950 via-primary-800 to-primary-950 shadow-lg shadow-primary-100 duration-300 h-40 border-none  rounded-lg text-primary-50 sm:w-[280px] lg:w-[300px]'}
              >
                <div className={'font-semibold md:text-left text-center md:m-0 m-4'}>
                  <div className={'w-full flex md:flex-row flex-col md:justify-between justify-center items-center text-center'}>
                    <p className={'text-lg text-primary-100'}>Total Users</p>
                    <FaUserPlus className={'md:text-5xl text-3xl'} />
                  </div>
                  <p className={'md:text-6xl text-5xl font-semibold'}>
                    {isLoading ? (
                      <Skeleton.Input active size={'small'} style={{width: 60}} />
                    ) : (
                      userCount
                    )}
                  </p>
                  
                </div>
              </Card>
            </Link>
          </div>

          {/* Total Orders */}
          <div>
            <Link to={'/admin/order'} className={'w-full sm:w-[280px]'}>
              <Card
                variant={'borderless'}
                className={'transition-all transform-gpu hover:scale-[1.04] bg-gradient-to-tr from-primary-950 via-primary-800 to-primary-950  shadow-lg shadow-primary-100 duration-300 h-40 border-none  rounded-lg text-primary-50 sm:w-[280px] lg:w-[300px]'}
              >
                <div className={'font-semibold md:text-left text-center  md:m-0 m-4'}>
                  <div className={'w-full flex md:flex-row flex-col md:justify-between justify-center items-center text-center'}>
                    <p className={'text-primary-100 text-lg'}>Total Orders</p>
                    <BsFillBoxSeamFill className={'md:text-5xl text-3xl'}  />
                  </div>
                  <p className={'md:text-6xl text-5xl font-semibold'}>
                    {isOrdersLoading ? (
                      <Skeleton.Input active size={'small'} style={{width: 60}} />
                    ) : (
                      orderCount
                    )}
                  </p>
                </div>
              </Card>
            </Link>
          </div>

          {/* Total Products */}
          <div>
            <Link to={'/admin/team/product'} className={'w-full '}>
              <Card
                variant={'borderless'}
                className={'transition-all transform-gpu hover:scale-[1.04] bg-gradient-to-tr from-primary-950 via-primary-800 to-primary-950 shadow-lg shadow-primary-100 duration-300 h-40 border-none  rounded-lg text-primary-50 sm:w-[380px] lg:w-[300px]'}
              >
                <div className={'font-semibold md:m-0 m-3 md:text-left text-center'}>
                  <div className={'w-full flex md:flex-row flex-col md:justify-between justify-center items-center text-center'}>
                    <p className={'text-primary-100 text-lg'}>Total Products</p>
                    <AiFillProduct 
                      className={'md:text-5xl text-3xl'}  />
                  </div>
                  <p className={'md:text-6xl text-5xl font-semibold'}>
                    {isProductsLoading ? (
                      <Skeleton.Input active size={'small'} style={{width: 60}} />
                    ) : (
                      productCount
                    )}
                  </p>
                </div>
              </Card>
            </Link>
          </div>

          {/* Low Stock Card */}
          <div>
            <Card
              variant={'borderless'}
              className={'transition-all transform-gpu hover:scale-[1.04] bg-gradient-to-tr from-primary-950 via-primary-800 to-primary-950 shadow-lg shadow-primary-100 duration-300 md:h-40 h-48 border-none  rounded-lg text-primary-50 sm:w-[480px] lg:w-[300px]'}
              onClick={() => setIsModalOpen(true)}
            >
              <div className={'font-semibold md:text-left text-center  md:m-0 m-4'}>
                <div className={'w-full flex md:flex-row flex-col md:justify-between justify-center items-center text-center'}>
                  <div className={'flex md:flex-row flex-col md:gap-2'}>
                    <p className={'text-primary-100 text-lg'}>Low Products </p>
                    <p className={'text-primary-100 text-lg'}>Stock</p>
                  </div>
                  <IoIosWarning 
                    className={'md:text-5xl text-3xl'}  />
                </div>
                <p className={'md:text-6xl text-5xl md:pl-0 font-semibold underline decoration-[#ff0000]'}>
                  {isProductsLoading ? (
                    <Skeleton.Input active size={'small'} style={{width: 60}} />
                  ) : (
                    lowStockCount
                  )}
                </p>
              </div>
            </Card>
          </div>

          <div className={'w-full grid grid-cols-1 lg:grid-cols-9 gap-4 md:gap-6 mt-10'}>

            {/*  Today's Orders */}
            <Card
              title={'Today\'s Orders'}
              extra={(
                <Button
                  className={'font-medium'}
                  type={'link'}
                  onClick={() => navigate('/admin/order')}
                >
      View All
                </Button>
              )}
              className={'shadow-md bg-[#f0f5ff] col-span-9 lg:col-span-4 rounded-xl'}
            >

              {/* Scroll Wrapper */}
              <div className={'overflow-x-auto'}>

                {/*  SAME SCROLL AREA */}
                <div className={'max-h-[300px] overflow-y-auto pr-1'}>

                  {/* Header Row (NOW INSIDE) */}
                  <div className={'min-w-[500px] grid grid-cols-8 text-primary-900 font-semibold text-center text-gray-600 border-b pb-2 mb-2 sticky top-0 bg-[#f0f5ff] z-10'}>
                    <p className={'col-span-2'}>Name</p>
                    <p className={'col-span-3'}>Transaction ID</p>
                    <p>Price</p>
                    <p className={'col-span-2'}>Status</p>
                  </div>

                  {/* Data */}
                  {todaysOrders.length === 0 ? (
                    <p className={'text-center py-4'}>No orders today</p>
                  ) : (
                    todaysOrders.map(order => (
                      <div
                        key={order.id}
                        className={'min-w-[500px] grid grid-cols-8 items-center text-center py-3 border-b border-primary-100 hover:bg-primary-50 transition'}
                      >

                        <p className={'text-sm text-gray-600 col-span-2 truncate'}>
                          {order.userInfo
                            ? `${order.userInfo.firstName} ${order.userInfo.lastName}`
                            : 'Unknown User'}
                        </p>

                        <p className={'font-semibold col-span-3 truncate'}>
                          {order.transactionId || order.id}
                        </p>

                        <p className={'text-sm whitespace-nowrap'}>
              ₹ {order.totalAmount}
                        </p>

                        <Tag
                          className={'text-center w-full col-span-2'}
                          color={getStatusColor(order.status)}
                        >
                          {order.status}
                        </Tag>
                      </div>
                    ))
                  )}

                </div>
              </div>
            </Card>

            {/* Orders Pending for Shipment */}
            <Card
              title={'Orders Pending for Shipment'}
              className={'shadow-md bg-[#f0f5ff] col-span-9 lg:col-span-5 rounded-xl'}
            >

              {/* Horizontal Scroll Wrapper */}
              <div className={'overflow-x-auto'}>

                {/* Same scroll area */}
                <div className={'max-h-[300px] overflow-y-auto pr-1'}>

                  {/* Header */}
                  <div className={'min-w-[600px] grid grid-cols-7 text-primary-900 font-semibold text-center border-b pb-2 mb-2 sticky top-0 bg-[#f0f5ff] z-10'}>
                    <p className={'col-span-2'}>Customer</p>
                    <p className={'col-span-2'}>Transaction ID</p>
                    <p>Price</p>
                    <p>Payment</p>
                    <p>Status</p>
                  </div>

                  {/* Data */}
                  {createdOrders.length === 0 ? (
                    <p className={'text-center py-4'}>No created orders</p>
                  ) : (
                    createdOrders.map(order => (
                      <div
                        key={order.id}
                        className={'min-w-[600px] grid grid-cols-7 items-center text-center py-3 border-b border-primary-100 hover:bg-primary-50 transition'}
                      >
                        
                        {/* Customer */}
                        <p className={'text-sm col-span-2 text-gray-600 truncate'}>
                          {order.userInfo
                            ? `${order.userInfo.firstName} ${order.userInfo.lastName}`
                            : 'Unknown'}
                        </p>

                        {/* ID */}
                        <p className={'font-semibold col-span-2 truncate'}>
                          {order.transactionId || order.id}
                        </p>

                        {/* Price */}
                        <p className={'whitespace-nowrap'}>
              ₹ {order.totalAmount}
                        </p>

                        {/* Payment */}
                        <p>
                          {order.paymentMethod === 'COD' ? 'COD' : 'Online'}
                        </p>

                        {/* Status Dropdown */}
                        <Dropdown
                          overlay={(
                            <Menu onClick={({key}) =>
                              handleStatusChange(order.id, order.status, key)
                            }>
                              {['CONFIRMED', 'PENDING', 'CANCELLED', 'SHIPPED', 'DELIVERED']
                                .filter(s => s !== order.status)
                                .map(s => (
                                  <Menu.Item key={s}>
                                    <Tag color={getStatusColor(s)}>{s}</Tag>
                                  </Menu.Item>
                                ))}
                            </Menu>
                          )}
                        >
                          <Tag
                            color={getStatusColor(order.status)}
                            className={'cursor-pointer w-fit mx-auto'}
                          >
                            {order.status} ▼
                          </Tag>
                        </Dropdown>

                      </div>
                    ))
                  )}
                </div>
              </div>
            </Card>
          </div>
        </div>

        <LowStockModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          lowStockProducts={lowStockProducts}
          isLoading={isProductsLoading}
        />
      </div>
    </ConfigProvider>
  );
};

export default UserCountScreen;
